import { arrayMove } from "@dnd-kit/sortable";

export interface ReorderableColumn {
  key: string;
  displayed?: boolean;
  disableColumnReorder?: boolean;
}

const NON_REORDERABLE_COLUMN_KEYS = new Set(["expander", "select", "selected"]);

export function isColumnReorderable(column: ReorderableColumn): boolean {
  return (
    !column.disableColumnReorder && !NON_REORDERABLE_COLUMN_KEYS.has(column.key)
  );
}

export function getReorderableColumnKeys<T extends ReorderableColumn>(
  columns: T[],
): string[] {
  return columns.filter(isColumnReorderable).map((column) => column.key);
}

export function isColumnReorderingEnabled({
  enableColumnReordering,
  reorderableColumnKeys,
}: {
  enableColumnReordering?: boolean;
  reorderableColumnKeys: string[];
}): boolean {
  return Boolean(enableColumnReordering && reorderableColumnKeys.length > 1);
}

export function getColumnPinsAfterReorder({
  activeKey,
  columnPins,
  overKey,
}: {
  activeKey: string;
  columnPins: Record<string, boolean>;
  overKey: string;
}): Record<string, boolean> {
  const activePinned = columnPins[activeKey] ?? false;
  const overPinned = columnPins[overKey] ?? false;

  if (activePinned === overPinned) {
    return columnPins;
  }

  return { ...columnPins, [activeKey]: overPinned };
}

export function reorderDisplayedColumns<T extends ReorderableColumn>({
  activeKey,
  displayedColumns,
  overKey,
}: {
  activeKey: string;
  displayedColumns: T[];
  overKey: string;
}): T[] {
  if (activeKey === overKey) {
    return displayedColumns;
  }

  const activeIndex = displayedColumns.findIndex(
    (column) => column.key === activeKey,
  );
  const overIndex = displayedColumns.findIndex(
    (column) => column.key === overKey,
  );

  if (activeIndex === -1 || overIndex === -1) {
    return displayedColumns;
  }

  return arrayMove(displayedColumns, activeIndex, overIndex);
}
